import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Table, Alert, Modal, Form, Row, Col, Badge, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faSearch, 
  faEye,
  faUser,
  faEnvelope,
  faPhone,
  faIdCard,
  faSignOutAlt,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { tenantService } from '../../services/tenantService';

const TenantManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userCompany, logout, currentUser, userRole } = useAuth();
  
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTenants, setFilteredTenants] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    idNumber: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, [userCompany]);

  // Handle delete flag from navigation
  useEffect(() => {
    if (location.state?.deleteTenantId) {
      const tenantToDelete = tenants.find(t => t.id === location.state.deleteTenantId);
      if (tenantToDelete) {
        setSelectedTenant(tenantToDelete);
        setShowDeleteModal(true);
      }
      // Clear the state
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, tenants, navigate]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTenants(tenants);
    } else {
      const filtered = tenants.filter(tenant =>
        tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.phone?.includes(searchTerm) ||
        tenant.idNumber?.includes(searchTerm)
      );
      setFilteredTenants(filtered);
    }
  }, [searchTerm, tenants]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      
      // Don't fetch if userCompany is not available yet
      if (!userCompany) {
        console.log('userCompany not available yet, skipping tenant fetch');
        setTenants([]);
        setFilteredTenants([]);
        return;
      }
      
      const tenantsData = await tenantService.getTenantsByCompany(userCompany);
      setTenants(tenantsData);
      setFilteredTenants(tenantsData);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setError('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      idNumber: '',
      address: '',
      emergencyContact: '',
      emergencyPhone: '',
      notes: ''
    });
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      console.log('=== BEFORE TENANT CREATION ===');
      console.log('Current user:', currentUser);
      console.log('Current user role:', userRole);
      console.log('Current user company:', userCompany);
      console.log('Creating tenant with userCompany:', userCompany);
      console.log('Form data:', formData);

      // Validate userCompany is available
      if (!userCompany) {
        console.error('userCompany is undefined or null!');
        setError('Company information not available. Please refresh the page and try again.');
        return;
      }

      // Check email availability first
      console.log('Checking email availability for:', formData.email);
      const emailCheck = await tenantService.checkEmailAvailability(formData.email, userCompany);
      console.log('Email availability check result:', emailCheck);
      
      if (!emailCheck.available) {
        setError(emailCheck.message);
        return;
      }

      const tenantData = {
        ...formData,
        companyId: userCompany
      };
      console.log('Final tenant data being sent:', tenantData);

      console.log('Calling tenantService.createTenant...');
      const newTenant = await tenantService.createTenant(tenantData);
      console.log('tenantService.createTenant returned:', newTenant);

      console.log('=== AFTER TENANT CREATION ===');
      console.log('New tenant created:', newTenant);
      console.log('Current user after creation:', currentUser);
      console.log('Current user role after creation:', userRole);
      console.log('Current user company after creation:', userCompany);

      setShowCreateModal(false);
      resetForm();
      
      console.log('Refreshing tenants list...');
      await fetchTenants();
      
      // Show success message with temporary password
      alert(`Tenant created successfully!\n\nLogin Credentials:\nEmail: ${formData.email}\nTemporary Password: ${formData.email}\n\nPlease inform the tenant to change their password upon first login.`);
    } catch (error) {
      console.error('Error creating tenant:', error);
      console.error('Error stack:', error.stack);
      
      // Provide more specific error messages
      if (error.message.includes('already exists')) {
        setError(error.message);
      } else if (error.message.includes('already in use')) {
        setError(`Email ${formData.email} is already in use by another account. Please use a different email address.`);
      } else {
        setError('Failed to create tenant: ' + (error.message || error));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditTenant = async (e) => {
    e.preventDefault();
    
    console.log('Handling edit tenant with formData:', formData);
    console.log('Selected tenant:', selectedTenant);
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      console.log('Calling tenantService.updateTenant with:', selectedTenant.id, formData);
      await tenantService.updateTenant(selectedTenant.id, formData);

      setShowEditModal(false);
      setSelectedTenant(null);
      resetForm();
      fetchTenants();
    } catch (error) {
      console.error('Error updating tenant:', error);
      setError('Failed to update tenant: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTenant = async () => {
    try {
      console.log('Handling delete tenant:', selectedTenant);
      setSaving(true);
      console.log('Calling tenantService.deleteTenant with:', selectedTenant.id);
      await tenantService.deleteTenant(selectedTenant.id);
      setShowDeleteModal(false);
      setSelectedTenant(null);
      fetchTenants();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      setError('Failed to delete tenant: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (tenant) => {
    console.log('Opening edit modal for tenant:', tenant);
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      idNumber: tenant.idNumber || '',
      address: tenant.address || '',
      emergencyContact: tenant.emergencyContact || '',
      emergencyPhone: tenant.emergencyPhone || '',
      notes: tenant.notes || ''
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (tenant) => {
    console.log('Opening delete modal for tenant:', tenant);
    setSelectedTenant(tenant);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/dashboard')}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Dashboard
          </Button>
          <div>
            <h2 className="mb-1">Tenant Management</h2>
            <p className="text-muted mb-0">Manage tenant profiles and information</p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Tenant
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search and Stats */}
      <Row className="mb-4">
        <Col lg={8}>
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search tenants by name, email, phone, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col lg={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h4 className="mb-1">{filteredTenants.length}</h4>
              <p className="text-muted mb-0">Total Tenants</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tenants Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <h5 className="mb-0">Tenant List</h5>
        </Card.Header>
        <Card.Body>
          {filteredTenants.length === 0 ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faUser} className="text-muted mb-3" size="3x" />
              <h5>No tenants found</h5>
              <p className="text-muted">
                {searchTerm ? 'No tenants match your search criteria.' : 'No tenants have been added yet.'}
              </p>
              {!searchTerm && (
                <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add First Tenant
                </Button>
              )}
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>ID Number</th>
                  <th>Emergency Contact</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faUser} className="text-primary me-2" />
                        <div>
                          <strong>{tenant.name}</strong>
                          {tenant.address && (
                            <div className="small text-muted">{tenant.address}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div>
                          <FontAwesomeIcon icon={faEnvelope} className="text-muted me-1" />
                          {tenant.email}
                        </div>
                        <div>
                          <FontAwesomeIcon icon={faPhone} className="text-muted me-1" />
                          {tenant.phone}
                        </div>
                      </div>
                    </td>
                    <td>
                      {tenant.idNumber ? (
                        <div>
                          <FontAwesomeIcon icon={faIdCard} className="text-muted me-1" />
                          {tenant.idNumber}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {tenant.emergencyContact ? (
                        <div>
                          <div>{tenant.emergencyContact}</div>
                          <div className="small text-muted">{tenant.emergencyPhone}</div>
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <Badge bg={tenant.status === 'active' ? 'success' : 'secondary'}>
                        {tenant.status}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => navigate(`/manager/tenants/${tenant.id}`)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => openEditModal(tenant)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => openDeleteModal(tenant)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create Tenant Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Tenant</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateTenant}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ID Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="idNumber"
                    value={formData.idNumber}
                    onChange={handleInputChange}
                    placeholder="Enter ID number"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter address"
                rows="2"
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Emergency Contact</Form.Label>
                  <Form.Control
                    type="text"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    placeholder="Emergency contact name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Emergency Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleInputChange}
                    placeholder="Emergency phone number"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes"
                rows="3"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Tenant'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Tenant Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Tenant</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditTenant}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ID Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="idNumber"
                    value={formData.idNumber}
                    onChange={handleInputChange}
                    placeholder="Enter ID number"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter address"
                rows="2"
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Emergency Contact</Form.Label>
                  <Form.Control
                    type="text"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    placeholder="Emergency contact name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Emergency Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleInputChange}
                    placeholder="Emergency phone number"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes"
                rows="3"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Updating...' : 'Update Tenant'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Tenant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the tenant <strong>"{selectedTenant?.name}"</strong>?</p>
          <p className="text-danger">
            <strong>Warning:</strong> This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteTenant}
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete Tenant'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TenantManagement; 